import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AspirantSignupResponse } from '@udbodhanam/shared';
import apiClient from '../services/apiClient';
import { useAuthStore } from '../stores/useAuthStore';
import ThemeToggle from '../components/UI/ThemeToggle';
import LanguageSelector from '../components/UI/LanguageSelector';
import RazorpayCheckout from '../components/payment/RazorpayCheckout';
import PlanConfirmation from '../components/payment/PlanConfirmation';

const ROLE_HOME: Record<string, string> = {
  ADMIN: '/admin',
  MENTOR: '/mentor',
  STUDENT: '/student',
};

export default function PaymentRequired() {
  const { t } = useTranslation(['auth', 'common']);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const logout = useAuthStore((s) => s.logout);
  const [checkoutData, setCheckoutData] = useState<AspirantSignupResponse | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(false);

  // The store (refreshed on every mount via AuthInitializer's GET /users/me)
  // is the source of truth — a refresh or mid-session payment completing must
  // not leave the user stuck on a stale blocked screen.
  if (user?.accountType !== 'PAID' || user?.paymentAccessReason == null) {
    return <Navigate to={ROLE_HOME[role ?? ''] ?? '/login'} replace />;
  }

  // Mirrors AspirantSignup.tsx's confirm-then-pay sequence (Story 10.2a) —
  // renewal reuses the same PlanConfirmation step signup already shows,
  // instead of jumping straight into the Razorpay modal.
  if (checkoutData && !confirmed) {
    return <PlanConfirmation checkoutData={checkoutData} onProceed={() => setConfirmed(true)} />;
  }

  if (checkoutData && confirmed) {
    return <RazorpayCheckout checkoutData={checkoutData} mode="renewal" showTourOnSuccess={user.paymentAccessReason === 'NEVER_PAID'} />;
  }

  const expired = user.paymentAccessReason === 'EXPIRED';
  const copyKey = expired ? 'subscriptionExpired' : 'paymentRequired';

  // Story 10.6 (AC4) — reuses the exact same order-creation + Checkout flow
  // as signup (POST /payments/renew calls the same AspirantSignupService
  // .createOrderAndPayment used by signup/reorder). Rendering RazorpayCheckout
  // below (mode="renewal") reuses that component as-is rather than a second
  // Razorpay integration.
  async function handleLogout() {
    await apiClient.post('/auth/logout').catch(() => undefined);
    logout();
  }

  async function handleStartCheckout() {
    setStarting(true);
    setStartError(false);
    try {
      const res = await apiClient.post<{ data: AspirantSignupResponse }>('/payments/renew');
      setCheckoutData(res.data.data);
    } catch {
      setStartError(true);
    } finally {
      setStarting(false);
    }
  }

  return (
    <main className="flex min-h-screen">
      {/* LEFT — orange hero panel */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -50, right: -50, width: 300, height: 300, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -180, left: -120, width: 380, height: 380, background: 'rgba(255,255,255,0.08)' }}
        />
        <div className="relative">
          <h1 className="font-display text-[56px] leading-[64px] font-semibold text-white tracking-[-0.01em]">
            Udbodhanam
          </h1>
          <p className="font-display italic text-white/90 text-2xl mt-3 font-medium">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>
      </div>

      {/* RIGHT — content panel */}
      <div className="w-full md:flex-1 bg-surface flex flex-col relative">
        <div className="absolute top-5 right-5 md:top-7 md:right-8 flex items-center gap-2 z-10">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 pb-10 pt-6 md:px-[72px] md:py-[60px]">
          <div className="md:hidden mb-10 text-center">
            <p className="font-display text-[34px] leading-tight font-semibold text-primary-600 tracking-[-0.01em]">Udbodhanam</p>
            <p className="font-display italic text-text-muted text-[15px] font-medium mt-1.5">&ldquo;Awakening through knowledge&rdquo;</p>
          </div>
          <div className="max-w-[440px] w-full mx-auto rounded-2xl shadow-xl bg-surface p-6 md:rounded-none md:shadow-none md:bg-transparent md:p-0">
            <h2 className="text-[36px] leading-[44px] font-bold text-earth-800 tracking-[-0.015em]">
              {t(`${copyKey}.title`)}
            </h2>
            <p className="text-earth-600 mt-1.5 text-sm">{t(`${copyKey}.subtitle`)}</p>

            {/* Context banner */}
            <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
              <p className="text-[13px] font-semibold text-primary-700">
                {t(`${copyKey}.contextTitle`)}
              </p>
              <p className="mt-1 text-[12px] text-primary-600 leading-relaxed">
                {t(`${copyKey}.contextBody`)}
              </p>
            </div>

            {startError && (
              <p className="mt-4 text-[13px] text-red-600">{t('payment.failedMessage')}</p>
            )}

            <button
              type="button"
              onClick={() => void handleStartCheckout()}
              disabled={starting}
              className="mt-6 w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 active:bg-primary-700 disabled:opacity-50"
            >
              {starting ? '…' : t(`${copyKey}.button`)}
            </button>

            <button
              type="button"
              onClick={() => void handleLogout()}
              className="mt-3 w-full rounded-lg border border-earth-300 py-2.5 text-sm font-semibold text-earth-900 hover:bg-earth-50 active:bg-earth-100 transition-colors"
            >
              {t('nav.signOut', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
